// «Compartir en…» — the page half of the Web Share Target (docs/PARITY.md P134; core/shareTarget.ts).
// sw.js parked the share in Cache Storage and opened `/?share=<id>`; this asks which conversation
// (the forward sheet's list: any team, most recently active first), then STAGES it there — files in
// that conversation's composer tray (uploading at once, like a pick), text appended to its draft —
// and opens it. Nothing is sent until the reader presses Enviar (the iOS share extension's rule).
import { useEffect, useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import { displayName } from '../../../core/channelListDerivation';
import * as ForwardPresentation from '../../../core/forwardPresentation';
import { composedText, isFreshShare, mergeIntoDraft, shareSummary, SHARE_CACHE, SHARE_PARAM, type SharedPayloadMeta } from '../../../core/shareTarget';
import type { ForwardData } from '../../../data/features/compose';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Button, Dialog, SearchInput } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import { addFiles } from '../attachments/tray';
import { PwaIcon } from './pwaIcons';
import '../compose/compose.css';
import './pwa.css';

interface Parked {
  id: string;
  meta: SharedPayloadMeta;
}

/** The share id in the address (then removed from it, so a reload never offers it twice). */
function takeShareId(): string | null {
  const url = new URL(window.location.href);
  const id = url.searchParams.get(SHARE_PARAM);
  if (!id) return null;
  url.searchParams.delete(SHARE_PARAM);
  history.replaceState(history.state, '', url.pathname + (url.search ? url.search : '') + url.hash);
  return /^[0-9a-z]{4,40}$/.test(id) ? id : null;
}

async function readParked(id: string): Promise<Parked | null> {
  if (!('caches' in window)) return null;
  const cache = await caches.open(SHARE_CACHE);
  const res = await cache.match(`/__share/${id}/meta`);
  if (!res) return null;
  try {
    return { id, meta: (await res.json()) as SharedPayloadMeta };
  } catch {
    return null;
  }
}

async function readFiles(parked: Parked): Promise<File[]> {
  const cache = await caches.open(SHARE_CACHE);
  const files: File[] = [];
  for (let i = 0; i < parked.meta.files.length; i++) {
    const res = await cache.match(`/__share/${parked.id}/file/${i}`);
    if (!res) continue;
    const blob = await res.blob();
    const info = parked.meta.files[i];
    files.push(new File([blob], info.name, { type: info.type || blob.type }));
  }
  return files;
}

async function dropParked(id: string): Promise<void> {
  try {
    const cache = await caches.open(SHARE_CACHE);
    for (const key of await cache.keys()) if (new URL(key.url).pathname.startsWith(`/__share/${id}/`)) await cache.delete(key);
  } catch {
    /* best effort */
  }
}

export function ShareTargetHost({ selectedChannelId }: { selectedChannelId: string | null }) {
  const shell = useShell();
  const [parked, setParked] = useState<Parked | null>(null);

  useEffect(() => {
    const id = takeShareId();
    if (!id) return;
    void readParked(id).then((p) => {
      if (!p || !isFreshShare(p.meta, Date.now())) {
        shell.toast(t('web.share.expired'), 'error');
        void dropParked(id);
        return;
      }
      if (!p.meta.files.length && !composedText(p.meta)) {
        shell.toast(t('web.share.empty'), 'error');
        void dropParked(id);
        return;
      }
      setParked(p);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!parked) return null;
  const close = () => {
    void dropParked(parked.id);
    setParked(null);
  };
  const stage = async (channelId: string, name: string) => {
    const files = await readFiles(parked);
    const text = composedText(parked.meta);
    if (text) {
      const snapshot = await data.call('sidebar').catch(() => null);
      const draft = snapshot?.drafts.find((d) => d.channelId === channelId && !d.rootId)?.message ?? '';
      await data.call('saveDraft', channelId, '', mergeIntoDraft(draft, text));
    }
    if (files.length) void addFiles(channelId, channelId, files.map((file) => ({ file, source: 'picker' as const })));
    const wasOpen = selectedChannelId === channelId;
    shell.openChannel(channelId);
    // A composer already on screen does not re-read its draft: hand it the text directly.
    if (wasOpen && text) window.dispatchEvent(new CustomEvent('satori:compose-insert', { detail: { channelKey: channelId, text } }));
    shell.toast(tf('web.share.staged', name));
    close();
  };
  return <SharePicker meta={parked.meta} onClose={close} onPick={(id, name) => void stage(id, name)} />;
}

function SharePicker({ meta, onClose, onPick }: { meta: SharedPayloadMeta; onClose(): void; onPick(channelId: string, name: string): void }) {
  const shell = useShell();
  const [raw, setRaw] = useState<ForwardData | null>(null);
  const [filter, setFilter] = useState('');
  const [highlight, setHighlight] = useState(0);
  useEffect(() => {
    void data.call('forwardData').then(setRaw, () => setRaw({ currentUserId: '', channels: [], users: [], participants: {}, teams: [] }));
  }, []);
  const model = useMemo(() => {
    if (!raw) return null;
    const usersById = new Map(raw.users.map((u) => [u.id, u]));
    const names: Record<string, string> = {};
    const slugs: Record<string, string> = {};
    for (const c of raw.channels) {
      names[c.id] = displayName(c, {
        currentUserId: raw.currentUserId,
        usersById,
        participantIds: raw.participants[c.id] ?? [],
        unknownUserFallback: c.type === 'G' ? t('channelList.unknownGroup') : t('channelList.unknownUser'),
      });
      slugs[c.id] = c.name;
    }
    const teamNames = Object.fromEntries(raw.teams.map((x) => [x.id, x.displayName]));
    return { targets: ForwardPresentation.targets(raw.channels, names, teamNames, shell.team.id), slugs };
  }, [raw, shell.team.id]);
  const rows = useMemo(() => (model ? ForwardPresentation.filtered(model.targets, filter, model.slugs) : []), [model, filter]);
  useEffect(() => setHighlight(0), [filter]);

  return (
    <Dialog
      title={t('share.title')}
      onClose={onClose}
      width={520}
      footer={
        <Button kind="quiet" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      }
    >
      <div className="share-preview" data-testid="share-preview">
        <span className="share-preview-icon">
          <PwaIcon name="share" size={18} />
        </span>
        <span className="share-preview-text">{shareSummary(meta)}</span>
      </div>
      <p className="share-hint">{t('web.share.pickerHint')}</p>
      <div
        onKeyDown={(e) => {
          if (!rows.length) return;
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => (h + (e.key === 'ArrowDown' ? 1 : rows.length - 1)) % rows.length);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const row = rows[Math.min(highlight, rows.length - 1)];
            onPick(row.channelId, row.name);
          }
        }}
      >
        <SearchInput value={filter} onChange={setFilter} placeholder={t('forward.filter')} autoFocus />
      </div>
      <div className="compose-forward-list" role="listbox" aria-label={t('share.title')}>
        {!model ? null : rows.length === 0 ? (
          <div className="compose-forward-empty">{model.targets.length === 0 ? t('forward.empty') : t('forward.noMatches')}</div>
        ) : (
          rows.map((row, i) => (
            <button
              key={row.channelId}
              type="button"
              data-index={i}
              className={`compose-forward-row${i === highlight ? ' highlight' : ''}`}
              role="option"
              aria-selected={i === highlight}
              onClick={() => onPick(row.channelId, row.name)}
            >
              <span className="glyph">
                <Icon name={row.type === 'P' ? 'lock' : row.type === 'D' ? 'person' : row.type === 'G' ? 'people' : 'hash'} size={16} />
              </span>
              <span className="names">
                <span className="name">{row.name}</span>
                {row.teamName ? <span className="team">{row.teamName}</span> : null}
              </span>
            </button>
          ))
        )}
      </div>
    </Dialog>
  );
}
